// Types for Admin -> Businesses -> All Businesses.
//
// GET /api/v1/businesses/list?page=&size=(&expired=true) - paginated. Same
// third envelope shape as business-subscriptions
// ({errors, status:{code}, contents}), but `contents` here is an
// array-wrapped SINGLE page object (see AdminBusinessListApiService's
// coerceSingle use), not a flat array of rows - the page object's own
// `content` field is the actual row array.

/** One row from the businesses list. Deliberately NOT the full
 *  BusinessProfile shape BusinessApiService.getBusinessById returns - this is
 *  a lighter admin-listing projection, same relationship AdminPendingBusiness
 *  has to PendingBusiness. */
export interface AdminBusinessListItem {
  businessId: string;
  businessName: string;
  creatorId: string;
  /** Null when nobody has claimed/logged in as the owner yet - distinct from
   *  creatorId, which is always present. */
  ownerId: string | null;
  ownerContactEmail: string | null;
  ownerContactPhone: string | null;
  /** Null when the business has never had a subscription at all, as opposed
   *  to one that already lapsed (a real past date). */
  subscriptionEnd: string | null;
  isOnboarded: boolean;
}

/** The page object nested inside the envelope's array-wrapped `contents`. */
export interface AdminBusinessListPage {
  content: AdminBusinessListItem[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

/** Matches the sample calls Sai verified this against (`size=20`). */
// TODO: temporarily raised from 20 so the whole list loads in one page (no "Load more").
// Restore to 20 once the list grows too large for a single fetch.
export const BUSINESS_LIST_PAGE_SIZE = 500;

/**
 * Result of POST .../extend-subscription?days=N (a manual admin override of
 * a business's subscription end date). The sample response Sai supplied
 * carries BOTH naming conventions for the before/after dates
 * (previousSubscriptionEnd/newSubscriptionEnd AND
 * subscriptionPreviousEnd/subscriptionNewEnd) - the convertor below reads
 * whichever pair is present, preferring the unprefixed one. `subscriptionId`
 * is optional since a business with no subscription at all presumably
 * wouldn't have one, though extending such a business is an edge case this
 * UI doesn't specifically guard against (the backend is the source of truth
 * on whether the call is valid).
 */
export interface AdminExtendSubscriptionResult {
  businessId: string;
  businessName: string;
  daysExtended: number;
  previousSubscriptionEnd: string | null;
  newSubscriptionEnd: string | null;
  subscriptionId: string | null;
}
